import { FlareDataVoidTags } from '@common/flare-data/constants/flare-data-void-tags.constant';
import { FlareDataSchema } from '@common/flare-data/schemas/flare-data-schema';
import { FlareSerializerExportOptions } from '@common/flare/types/to-flare-xml-options.type';
import { defaultToFlareXML } from '@common/flare/util/prosemirror-flare-xml';
import { DocSizeError } from '@common/html/constants/doc-size-error.constant';
import { NodeType } from '@common/html/enums/node-type.enum';
import { SimpleDomDocument } from '@common/html/simple-dom/node';
import { parseFromString } from '@common/html/simple-dom/parse';
import { ValidationOptions } from '@common/html/types/serialization-options.type';
import { stripBOM } from '@common/html/util/xml';
import { CustomError } from '@common/util/custom-error';
import { byteSize } from '@common/util/utf';
import { Fragment, ProseMirrorNode, Slice, DOMParser as pmDOMParser } from 'prosemirror-model';

export class FlareDataXmlSerializer {
  codeToDoc(code: string, schema: FlareDataSchema, validate?: (xmlDoc: SimpleDomDocument) => void): ProseMirrorNode {
    code = stripBOM(code);

    // Throw an error if the doc is too large
    this.throwIfDocTooLarge(code, schema.docMaxCharLength);

    // Throw an error if the doc is empty or doesn't start with an xml declaration
    if (!code || !code.startsWith('<?xml ')) {
      throw new Error('The document must begin with an xml declaration');
    }

    // Convert the xml string into an xml dom
    const xmlDoc = parseFromString(code, 'text/xml', {
      // Enable validation with strictMode
      strictMode: true,
      // Register the MadCap void tags so they are serialized correctly
      registerVoidTags: FlareDataVoidTags,
      // Register elements with custom outerHTML implementations
      registerElements: {
        // Because comments are replaced with flareDataComment nodes during the parse we need to serialize them back to regular comments in their outerHTML
        'flareDataComment': {
          outerHTML(node) {
            return `<!--${node.textContent}-->`;
          }
        }
      },
      // Process nodes after they have been parsed
      onNode: (node) => {
        // Convert comment nodes to flareDataComment nodes
        if (node.nodeType === NodeType.COMMENT_NODE) {
          const comment = node.ownerDocument.createElement('flareDataComment');
          comment.appendChild(node.ownerDocument.createTextNode(node.nodeValue));
          node.replaceWith(comment);
          node = comment;
        }
      }
    });

    if (typeof validate === 'function')
      validate(xmlDoc);

    // Convert the xml dom into a prosemirror doc
    const pmDoc = pmDOMParser.fromSchema(schema).parse(xmlDoc as unknown as Document);

    return pmDoc;
  }

  nodeToCode(node: ProseMirrorNode, schema: FlareDataSchema, options?: FlareSerializerExportOptions): string {
    return this.toFlareXML(node, schema, options);
  }

  parseSlice(xmlNode, schema: FlareDataSchema): Slice {
    // Convert the xml dom into a prosemirror doc
    const slice = pmDOMParser.fromSchema(schema).parseSlice(xmlNode as unknown as Document);
    return slice;
  }

  fragmentToCode(fragment: Fragment, schema: FlareDataSchema, options?: FlareSerializerExportOptions): string {
    return this.toFlareXML(fragment, schema, options);
  }

  // Validates flare code. Returns an error object if the code is invalid. Otherwise returns null.
  validate(code: string, schema: FlareDataSchema, options?: ValidationOptions): Error | null {
    // Validate the code by running the parser
    try {
      this.codeToDoc(code, schema);
    } catch (ex) {
      return ex;
    }

    return null;
  }

  private toFlareXML(nodeOrFragment: ProseMirrorNode | Fragment, schema: FlareDataSchema, options?: FlareSerializerExportOptions): string {
    let xml = '';

    if (nodeOrFragment instanceof Fragment) {
      nodeOrFragment.forEach(node => xml += (node.type.spec.toFlareXML || defaultToFlareXML)(node, options ?? {}));
    } else {
      xml = (nodeOrFragment.type.spec.toFlareXML || defaultToFlareXML)(nodeOrFragment, options ?? {});
    }

    this.throwIfDocTooLarge(xml, schema.docMaxCharLength);

    return xml;
  }

  private throwIfDocTooLarge(markup: string, maxDocCharLength: number) {
    if (typeof maxDocCharLength === 'number' && markup?.length > maxDocCharLength) {
      const error = new CustomError(DocSizeError, `Cannot load document, document is ${markup.length} characters long and must be ${maxDocCharLength} characters or less in length.`);
      (error as any).docLength = markup.length;
      (error as any).docSize = byteSize(markup);
      throw error;
    }
  }
}
